import { CatalogResourceDc, ExtendedProjectInfoDc, OwnerFilter } from "@evergis/api";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { ProjectInfoBlock } from "../../components/Dialogs/EditResource/components/Project/Info/types";

import { updateProject as updateCurrentProject } from "../../ducks/project/project";
import { api } from "../../evergis/api";
import { filesCatalog } from "../../resources/catalog";
import { project, projectInfosWithDeps, projects, updateProject } from "../../resources/projects";
import { useNotification } from "../notifications/useNotification";
import { useLoading } from "../useLoading";
import { useTrigger } from "../useTrigger";

export const useProjectMetaUpdate = (
  projectInfo: Partial<ExtendedProjectInfoDc>,
  meta: CatalogResourceDc,
  info: ProjectInfoBlock[],
) => {
  const { t } = useTranslation("dialogs");
  const { name: alias, description, parentId, tags } = meta;
  const dispatch = useDispatch();
  const [updateMutation, { data: projectUpdate, loading: projectUpdating, error: projectUpdateError }] =
    updateProject.use();
  const updating = useLoading(projectUpdating, !!projectUpdate);
  const setCurrentProject = useCallback(() => {
    dispatch(updateCurrentProject(projectUpdate));
  }, [dispatch, projectUpdate]);

  const update = useCallback(async () => {
    if (projectInfo?.parentId !== parentId) {
      await api.catalog.moveResource(projectInfo.resourceId, { targetResource: parentId });
      filesCatalog.invalidate({ ownerFilter: OwnerFilter.My, parentId });
      filesCatalog.invalidate({ ownerFilter: OwnerFilter.My, parentId: projectInfo?.parentId });
    }

    return updateMutation({
      input: {
        ...projectInfo,
        alias,
        description,
        parentId,
        tags,
        content: { ...projectInfo.content, projectInfo: info },
      },
    });
  }, [updateMutation, projectInfo, alias, description, info, tags, parentId]);

  const resetProject = useCallback(() => {
    project.invalidate(projectInfo.name);
    projectInfosWithDeps.invalidateAll();
    projects.invalidateAll();
  }, [projectInfo]);

  useTrigger(projectUpdate && !updating, resetProject);
  useTrigger(projectUpdate && !updating, setCurrentProject);

  useNotification(projectUpdate, {
    success: true,
    title: t("editResource.changesApplied"),
    description: alias,
  });

  useNotification(projectUpdateError, {
    error: true,
    title: t("editResource.projectHasNotBeenUpdated"),
    description: `${alias}. ${t("editResource.tryAgainOrCheckData")}`,
  });

  return useMemo(() => ({ update, projectUpdate, updating }), [update, projectUpdate, updating]);
};
